/* -------------------------------------------------------------------------
 * Sayuri signature · maple-fall  (Momiji Manju)
 * -------------------------------------------------------------------------
 * Self-registers with window.SayuriAnimations on load. Renders Momiji's
 * full atmosphere — TWO layers of falling particles in one mount:
 *
 *   1. MAPLE LEAVES (primary signature — autumn)
 *      ~14 detailed palmate maple leaves (3 shape variants, 11-stop
 *      autumn palette with gradients, radial veins, stem) drifting on
 *      3 wind paths. Size-speed coupled — bigger leaves drop faster.
 *      Spin direction and speed are fully random per leaf.
 *
 *   2. PETAL ATMOSPHERE (secondary — spring ghost)
 *      ~18 mixed cherry-blossom particles — ~45% full 5-petal whole
 *      blossoms, ~55% single drifting petals with midrib vein (from
 *      design PetalShape). White + pale-pink palette. Slower + more
 *      serene motion than the maples. Sakura-in-autumn is intentionally
 *      fantastical — Momiji's scent pyramid has cherry blossom as a top
 *      note, so this layer surfaces that nostalgic dual-season feeling.
 *
 * Both layers share the same fixed full-viewport container
 * (.sayuri-signature-layer). Motion lives on each particle as inline
 * `animation:` referencing this file's injected keyframes.
 *
 * Scent character: "Maple leaves at dusk, warm rice cake, the last day
 * of autumn." Maples give structure + color; blossoms + petals add the
 * spring memory carried in the scent's top notes.
 * ----------------------------------------------------------------------- */
(function () {
  'use strict';
  var SA = window.SayuriAnimations;
  if (!SA) { return; } // core failed to load; nothing to do

  // ════════════════════════════════════════════════════════════════════
  // Layer 1 — Maple leaves (primary signature)
  // ════════════════════════════════════════════════════════════════════
  //
  // Three palmate shape variants (real Acer palmatum — momiji — has 5–7
  // narrow pointed lobes, not a symmetric 11-point star). Each leaf picks
  // one shape at mount time plus a color pair drawn from an autumn
  // gradient (deep crimson → brick → orange → amber → gold → pink →
  // blush). ~60% of leaves get a vertical linear-gradient fill so the
  // blade reads as a 3D surface turning in flight; the rest get a solid
  // tone. Veins radiate from the petiole, a short stem trails below.
  // Each path is a 10-14 vertex polygon traced lobe-tip → sinus → next
  // lobe-tip, so every lobe tapers to a sharp point and every sinus is
  // a deep V-cut (matching real Acer palmatum geometry). Straight L
  // lines only — no smoothing — so corners stay crisp.
  var MAPLE_SHAPES = [
    // Variant 0 — classic 5-lobe palmate. Tips at (0,-14), (11,-4),
    // (9,8), (-9,8), (-11,-4). Sinuses cut back to ~4 units from center.
    'M0,-14 L3,-5 L11,-4 L5,2 L9,8 L0,5 L-9,8 L-5,2 L-11,-4 L-3,-5 Z',
    // Variant 1 — 7-lobe deeply dissected. Adds middle lobes at ±(11,2)
    // and tighter sinuses for a "threadleaf" momiji feel.
    'M0,-14 L2,-6 L9,-7 L7,-2 L11,2 L7,5 L7,9 L0,6 L-7,9 L-7,5 L-11,2 L-7,-2 L-9,-7 L-2,-6 Z',
    // Variant 2 — narrow 5-lobe with exaggerated sharp points. Lobe tips
    // stretched further out and sinuses cut deeper for drama.
    'M0,-14 L2,-3 L13,-3 L4,2 L12,9 L3,4 L3,12 L0,5 L-3,12 L-3,4 L-12,9 L-4,2 L-13,-3 L-2,-3 Z'
  ];

  // Autumn progression — ordered dark→light so adjacent-index pairs form
  // natural gradients (deep red blending to brick, gold to amber, etc.).
  // Index 0..10, a gradient usually pairs i with i+1 or i+2.
  var AUTUMN_COLORS = [
    '#7A1625', // deep crimson
    '#A82D22', // brick red
    '#C03528', // red
    '#D94A2C', // red-orange
    '#E8742E', // orange
    '#E8A83C', // warm amber
    '#C49540', // old gold
    '#A98C4A', // muted yellow-brown
    '#C56D8B', // dusty pink
    '#E8A7B5', // pale rose
    '#F5D4DA', // very pale blush
    '#FFE8EE', // pale petal pink
    '#FFFFFF'  // pure white (porcelain highlight / late-season bleached leaf)
  ];

  function buildMapleLeaves(el, ctx) {
    // Intensity tiers match design file: whisper 9, soft 14, lush 22.
    var intensity = ctx.intensity;
    var n = intensity === 'lush' ? 22 : intensity === 'whisper' ? 9 : 14;
    // Mobile CWV guard: halve particle count below 768px to protect INP/battery.
    if (window.innerWidth < 768) { n = Math.ceil(n / 2); }

    // Real-photo manifest (populated by Liquid when the theme has the
    // momiji-leaf-*.png assets). When present, each particle renders as
    // a photograph; when absent, fall back to the procedural SVG path.
    var assets = (typeof window !== 'undefined') && window.SAYURI_MOMIJI_ASSETS;
    var leafImages = (assets && assets.leaves) || [];

    // Dedicated PRNG seed so adding dust doesn't shuffle maple layout.
    var rand = ctx.seed('maple-leaves'.charCodeAt(0) * 131 + 13 * 7919);

    var frag = document.createDocumentFragment();
    for (var i = 0; i < n; i++) {
      var left = rand() * 100;
      var size = 18 + rand() * 20;      // 18–38px
      var delay = -rand() * 32;
      var opacity = 0.55 + rand() * 0.4; // 0.55–0.95

      // Size → fall-speed coupling. Bigger leaves drop faster; smaller
      // ones drift down slowly. Maps size (18–38px) inversely to duration
      // with a small jitter so same-sized leaves aren't perfectly in
      // lockstep. Slowed for a gentler autumn drift: biggest ~27s,
      // smallest ~44s.
      var sizeFactor = (size - 18) / 20;          // 0..1
      var dur = 27 + (1 - sizeFactor) * 16 + rand() * 2;

      // Shape: random pick from 3 palmate variants.
      var shape = MAPLE_SHAPES[Math.floor(rand() * MAPLE_SHAPES.length)];

      // Color: primary + optional gradient partner. Most leaves get a
      // gradient; ~40% stay flat for visual variety.
      var useGradient = rand() > 0.4;
      var c1Idx = Math.floor(rand() * AUTUMN_COLORS.length);
      // Gradient partner drifts 1–3 indices (either direction) for a
      // natural tone shift within the same hue family.
      var drift = 1 + Math.floor(rand() * 3);
      var direction = rand() > 0.5 ? 1 : -1;
      var c2Idx = Math.max(0, Math.min(AUTUMN_COLORS.length - 1, c1Idx + drift * direction));
      var c1 = AUTUMN_COLORS[c1Idx];
      var c2 = AUTUMN_COLORS[c2Idx];
      // Gradient orientation varies so not every leaf reads "top-dark
      // bottom-light". ~30% get a reversed vertical for backlit feel.
      var gradFlip = rand() > 0.7;

      // Fall path still cycles through 3 wind curves (i % 3) so all
      // three drift directions get evenly represented even in a sparse
      // flurry. But spin direction + spin speed are fully random per
      // leaf — the old `i % 2` alternation gave a subtle synchronized
      // feel where adjacent leaves always span opposite ways.
      var fallVariant = i % 3;
      var spinVariant = rand() > 0.5 ? 0 : 1;
      var swayDur = 5 + rand() * 3;       // 5–8s
      var spinDur = 6 + rand() * 8;       // 6–14s

      var p = document.createElement('div');
      p.className = 'sayuri-maple';
      p.style.position = 'absolute';
      p.style.top = '-40px';
      p.style.left = left + 'vw';
      p.style.width = size + 'px';
      p.style.height = size + 'px';
      p.style.opacity = opacity;
      p.style.animation =
        'mpl-fall-' + fallVariant + ' ' + dur + 's linear ' + delay + 's infinite, ' +
        'mpl-sway ' + swayDur + 's ease-in-out infinite';

      // Real photograph path — pick a leaf image from the per-scent
      // manifest and render as an <img>. The spin animation is applied
      // to the <img> itself so rotation happens around its own center.
      // `object-fit: contain` prevents stretching for non-square source
      // images (real photos have varying aspect ratios).
      if (leafImages.length > 0) {
        var leafUrl = leafImages[Math.floor(rand() * leafImages.length)];
        p.innerHTML =
          '<img src="' + leafUrl + '" alt="" draggable="false" ' +
          'style="width:100%;height:100%;display:block;object-fit:contain;' +
          'animation: mpl-spin-' + spinVariant + ' ' + spinDur + 's linear infinite;">';
      } else {
        // Procedural fallback — the existing SVG palmate when no real
        // photographs are available (e.g. other scents reusing this
        // module, or a theme without the momiji-leaf-*.png assets).
        var gradId = 'mpl-grad-' + i;
        var fillAttr = useGradient ? 'url(#' + gradId + ')' : c1;
        var gradientDefs = useGradient
          ? '<defs><linearGradient id="' + gradId + '" x1="0" ' +
              'y1="' + (gradFlip ? 12 : -14) + '" ' +
              'y2="' + (gradFlip ? -14 : 12) + '" ' +
              'gradientUnits="userSpaceOnUse">' +
              '<stop offset="0%" stop-color="' + c1 + '"/>' +
              '<stop offset="100%" stop-color="' + c2 + '"/>' +
            '</linearGradient></defs>'
          : '';

        p.innerHTML =
          '<svg viewBox="-14 -16 28 34" xmlns="http://www.w3.org/2000/svg" ' +
          'style="width:100%;height:100%;display:block;overflow:visible;' +
          'animation: mpl-spin-' + spinVariant + ' ' + spinDur + 's linear infinite;">' +
            gradientDefs +
            '<path d="' + shape + '" fill="' + fillAttr + '" ' +
              'stroke="rgba(40,10,20,0.35)" stroke-width="0.4" ' +
              'stroke-linejoin="miter" stroke-miterlimit="2"/>' +
            '<g stroke="rgba(40,10,20,0.3)" stroke-width="0.5" fill="none" stroke-linecap="round">' +
              '<line x1="0" y1="2" x2="0" y2="-11"/>' +
              '<line x1="0" y1="2" x2="7" y2="-7"/>' +
              '<line x1="0" y1="2" x2="9" y2="3"/>' +
              '<line x1="0" y1="2" x2="-7" y2="-7"/>' +
              '<line x1="0" y1="2" x2="-9" y2="3"/>' +
            '</g>' +
            '<line x1="0" y1="9" x2="0" y2="15" ' +
              'stroke="rgba(60,20,25,0.55)" stroke-width="0.9" stroke-linecap="round"/>' +
          '</svg>';
      }

      frag.appendChild(p);
    }
    el.appendChild(frag);
  }

  // ════════════════════════════════════════════════════════════════════
  // Layer 2 — Petal atmosphere (whole cherry blossoms + single petals)
  // ════════════════════════════════════════════════════════════════════
  //
  // Each particle picks at mount time between two silhouettes:
  //
  //   (a) 5-petal whole blossom (sakura in full bloom) — bigger, slower
  //   (b) Single drifting petal (individual sakura lobe with midrib
  //       vein from design PetalShape, line ~1897) — smaller, faster spin
  //
  // Sakura (spring) and momiji (autumn) seasons are six months apart —
  // mixing them here is intentionally fantastical. The Momiji scent's
  // top note IS cherry blossom, so the layer reads as "the fragrance
  // carrying a ghost of spring through the autumn fall."
  function buildPetalAtmosphere(el, ctx) {
    var intensity = ctx.intensity;
    var n = intensity === 'lush' ? 24 : intensity === 'whisper' ? 12 : 18;
    // Mobile CWV guard: halve dust count below 768px to protect INP/battery.
    if (window.innerWidth < 768) { n = Math.ceil(n / 2); }

    // Real-photo manifests — blossoms (whole flowers) and petals
    // (single lobes). When available each particle renders as a
    // photograph; when absent, fall back to the procedural SVG.
    var assets = (typeof window !== 'undefined') && window.SAYURI_MOMIJI_ASSETS;
    var blossomImages = (assets && assets.blossoms) || [];
    var petalImages = (assets && assets.petals) || [];

    // Blossom/petal palette — white + pink spectrum. 4 tones so the
    // atmosphere reads varied rather than monochrome.
    //   #FFFFFF  pure white (highlight)
    //   #FFE8EE  pale petal pink
    //   #F7D9DC  warm cream-pink
    //   #FFC4D1  deeper petal pink (design accent-soft range)
    var colors = ['#FFFFFF', '#FFE8EE', '#F7D9DC', '#FFC4D1'];

    var rand = ctx.seed('maple-petals'.charCodeAt(0) * 131 + 13 * 7919);

    var frag = document.createDocumentFragment();
    for (var i = 0; i < n; i++) {
      var left = rand() * 100;
      var isBlossom = rand() > 0.55;       // ~45% blossoms, ~55% petals

      var size, dur, spinDur;
      if (isBlossom) {
        size = 12 + rand() * 10;           // 12–22px
        dur = 24 + rand() * 16;            // 24–40s (serene float)
        spinDur = 14 + rand() * 8;         // 14–22s (slow rotation)
      } else {
        size = 7 + rand() * 8;             // 7–15px
        dur = 20 + rand() * 14;            // 20–34s
        spinDur = 8 + rand() * 8;          // 8–16s (petals twirl faster)
      }

      var delay = -rand() * 40;
      var color = colors[Math.floor(rand() * colors.length)];
      var opacity = 0.5 + rand() * 0.4;    // 0.5–0.9

      var fallVariant = i % 3;
      var spinReverse = rand() > 0.5 ? ' reverse' : '';
      var swayDur = 6 + rand() * 3;        // 6–9s random

      var p = document.createElement('div');
      p.className = 'sayuri-maple-petal';
      p.style.position = 'absolute';
      p.style.top = '-30px';
      p.style.left = left + 'vw';
      p.style.width = size + 'px';
      p.style.height = size + 'px';
      p.style.opacity = opacity;
      p.style.animation =
        'mpl-dust-fall-' + fallVariant + ' ' + dur + 's linear ' + delay + 's infinite, ' +
        'mpl-dust-sway ' + swayDur + 's ease-in-out infinite';

      // Real-photo path per particle type. Each particle picks one
      // image from its pool; duplicates across particles are fine (the
      // browser caches by URL so no extra requests).
      var pool = isBlossom ? blossomImages : petalImages;

      if (pool.length > 0) {
        var imgUrl = pool[Math.floor(rand() * pool.length)];
        p.innerHTML =
          '<img src="' + imgUrl + '" alt="" draggable="false" ' +
          'style="width:100%;height:100%;display:block;object-fit:contain;' +
          'animation: mpl-dust-spin ' + spinDur + 's linear infinite' + spinReverse + ';">';
      } else {
        // Procedural SVG fallback.
        var shape;
        if (isBlossom) {
          // 5-petal whole blossom — 5 copies of one petal path rotated
          // 72° apart. Matches design file line 3531.
          var inner = '';
          var angles = [0, 72, 144, 216, 288];
          for (var a = 0; a < angles.length; a++) {
            inner +=
              '<path d="M0,-7 C 2,-6.5 2.8,-3.5 2,0 C 1.7,1.8 0.8,2.8 0,4 C -0.8,2.8 -1.7,1.8 -2,0 C -2.8,-3.5 -2,-6.5 0,-7 Z" ' +
              'fill="' + color + '" transform="rotate(' + angles[a] + ')"/>';
          }
          shape = '<g>' + inner + '</g>';
        } else {
          // Single sakura petal with midrib vein (design file PetalShape
          // line ~1897). Soft plum outline + subtle center crease.
          shape =
            '<path d="M0,-10 C 3.6,-9 5,-5 4,0 C 3.2,3 1.6,5 0,7 C -1.6,5 -3.2,3 -4,0 C -5,-5 -3.6,-9 0,-10 Z" ' +
              'fill="' + color + '" stroke="rgba(164,58,114,0.25)" stroke-width="0.6"/>' +
            '<path d="M0,-6 Q 1.5,-2 0,5" fill="none" ' +
              'stroke="rgba(164,58,114,0.2)" stroke-width="0.5"/>';
        }
        p.innerHTML =
          '<svg viewBox="-10 -10 20 20" xmlns="http://www.w3.org/2000/svg" ' +
          'style="width:100%;height:100%;display:block;' +
          'animation: mpl-dust-spin ' + spinDur + 's linear infinite' + spinReverse + ';">' +
            shape +
          '</svg>';
      }

      frag.appendChild(p);
    }
    el.appendChild(frag);
  }

  // ════════════════════════════════════════════════════════════════════
  // Registration
  // ════════════════════════════════════════════════════════════════════
  SA.register('maple-fall', {
    mount: function (el, ctx) {
      buildMapleLeaves(el, ctx);
      buildPetalAtmosphere(el, ctx);
    },
    css: [
      // ── Maple motion (design file lines 986–991) ──
      '@keyframes mpl-fall-0 { 0%{transform:translate3d(0,0,0);} 100%{transform:translate3d(60px,110vh,0);} }',
      '@keyframes mpl-fall-1 { 0%{transform:translate3d(0,0,0);} 100%{transform:translate3d(-80px,110vh,0);} }',
      '@keyframes mpl-fall-2 { 0%{transform:translate3d(0,0,0);} 100%{transform:translate3d(30px,110vh,0);} }',
      '@keyframes mpl-spin-0 { from{transform:rotate(0);} to{transform:rotate(360deg);} }',
      '@keyframes mpl-spin-1 { from{transform:rotate(0);} to{transform:rotate(-360deg);} }',
      '@keyframes mpl-sway   { 0%,100%{margin-left:0;} 50%{margin-left:40px;} }',
      // ── Dust motion (gentler drift, smaller sway, slower spin) ──
      '@keyframes mpl-dust-fall-0 { 0%{transform:translate3d(0,0,0);} 100%{transform:translate3d(30px,110vh,0);} }',
      '@keyframes mpl-dust-fall-1 { 0%{transform:translate3d(0,0,0);} 100%{transform:translate3d(-50px,110vh,0);} }',
      '@keyframes mpl-dust-fall-2 { 0%{transform:translate3d(0,0,0);} 100%{transform:translate3d(10px,110vh,0);} }',
      '@keyframes mpl-dust-spin   { from{transform:rotate(0);} to{transform:rotate(360deg);} }',
      '@keyframes mpl-dust-sway   { 0%,100%{margin-left:0;} 50%{margin-left:18px;} }'
    ].join('')
  });
})();
